import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpFromBracket,
  faPenToSquare,
} from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { styled } from '@components-shared/src/stitches';
import { QueueSheet } from '@components/AudioPlayer/QueueSheet';
import { usePlayerTrackInfo } from '@components/AudioPlayer/usePlayerTrackInfo';
import { AddToPlaylist } from '@components/icons/AudioController/AddToPlaylist';
import { QueueIcon } from '@components/icons/AudioController/Queue';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { AddToPlaylistModal } from '@components/modals/AddToPlaylistModal/AddToPlaylistModal';
import { FeedPostModal } from '@components/modals/FeedPostModal';
import { UnlockFeedModal } from '@components/modals/FeedPostModal/UnlockFeedModal';
import { ShareModalRelease } from '@components/modals/ShareModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import { useBackendUserPersistedData } from '@hooks/useBackendUserPersistedData';
import { trackClickEvent } from '@lib/analytics';
import { useAudioController } from '@store/audio/AudioController';
import { FlexBetweenWrapper } from '../shared';

export const ReleaseActions = ({ closePlayer }: { closePlayer: () => void }) => {
  const { openModal } = ModalContext.useContainer();
  const { activeTrackId, activeTrackReleaseInfo, feedPostInfo } = useAudioController();
  const { isAuthenticatedWithAPI, authUser } = useAuth();
  const { login } = useConnectedAuth();
  const { openToast } = ToastContext.useContainer();
  const [isQueueOpen, setQueueOpen] = React.useState<boolean>(false);

  const { hasFeedAccess } = useBackendUserPersistedData();

  const hasPostingAccess = hasFeedAccess.type === 'has-access';

  const releaseId = activeTrackReleaseInfo?.id;

  const onOpenQueue = () => setQueueOpen(true);
  const onCloseQueue = () => setQueueOpen(false);

  const { releaseInfo } = usePlayerTrackInfo({
    trackId: activeTrackId,
    shouldFetch: true,
  });

  const onShareClick = () => {
    if (!releaseInfo) return;
    trackClickEvent({
      event: 'Share Modal Opened',
      properties: {
        associationId: releaseInfo.id,
        associationType: 'Release',
        sourceComponent: 'Audio Player',
      },
    });
    openModal(ModalType.SHARE, {
      body: <ShareModalRelease releaseId={releaseInfo.id} />,
    });
  };

  const onAddToPlaylistClick = () => {
    if (!isAuthenticatedWithAPI) {
      trackClickEvent({
        event: 'Sign in Clicked',
        properties: {
          sourceComponent: 'Add to Playlist Modal',
        },
      });
      login();
      return;
    }

    if (!releaseInfo?.id) {
      return openToast({
        text: 'This track is not available for playlisting yet.',
        variant: 'error',
      });
    }
    trackClickEvent({
      event: 'Add to Playlist Clicked',
      properties: {
        sourceComponent: 'Audio Player',
        associationId: releaseInfo.id,
        associationType: 'Release',
      },
    });
    return openModal(ModalType.ADD_TO_PLAYLIST, {
      body: <AddToPlaylistModal releaseId={releaseInfo.id} />,
    });
  };

  const onPostButtonClick = () => {
    if (!authUser) {
      login();
    } else if (feedPostInfo) {
      trackClickEvent({
        event: 'Create Post Clicked',
        properties: {
          sourceComponent: 'Audio Player Post Button',
          associationId: releaseId,
          associationType: 'Release',
        },
      });

      if (hasPostingAccess) {
        openModal(ModalType.FEED_POST, {
          body: (
            <FeedPostModal
              key={releaseId}
              preselectedResource={{ type: 'release', release: feedPostInfo }}
              sourceLocation="Audio Player Post Button"
            />
          ),
        });
      } else {
        openModal(ModalType.UNLOCK_FEED_POST, {
          body: (
            <UnlockFeedModal
              key={`unlock-song-feed-${releaseId}`}
              preselectedResource={{ type: 'release', release: feedPostInfo }}
              sourceLocation="Audio Player Post Button"
            />
          ),
        });
      }
      closePlayer();
    }
  };

  return (
    <>
      <FlexBetweenWrapper>
        <div className="flex gap-6">
          <IconButton
            variant="inverted"
            disabled={!releaseInfo}
            iconSize={20}
            size={28}
            icon={<FontAwesomeIcon icon={faArrowUpFromBracket} />}
            onClick={onShareClick}
          />
          <IconButton
            variant="inverted"
            disabled={!releaseInfo}
            iconSize={20}
            size={28}
            icon={<FontAwesomeIcon icon={faPenToSquare} />}
            onClick={onPostButtonClick}
          />
        </div>

        <Wrapper>
          <StyledIconButton
            variant="inverted"
            iconSize={20}
            size={28}
            icon={<AddToPlaylist />}
            onClick={onAddToPlaylistClick}
          />
          <StyledIconButton
            variant="inverted"
            iconSize={20}
            size={28}
            icon={<QueueIcon />}
            onClick={onOpenQueue}
          />
        </Wrapper>
      </FlexBetweenWrapper>

      <QueueSheet isOpen={isQueueOpen} onClose={onCloseQueue} />
    </>
  );
};

const Wrapper = styled('div', {
  display: 'flex',
  gap: 24,
});

const StyledIconButton = styled(IconButton, {
  svg: {
    strokeWidth: 2.5,
  },
});
